import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, MessageSquareMore } from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import config from "../config/config";
import { clearStoredAuth } from "../utils/authSession";

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchJson(path) {
  const response = await fetch(`${config.API_BASE_URL}${path}`, { headers: getAuthHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return data;
}

export default function ApplicantMessages() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  useEffect(() => {
    document.title = "Experts4Europe | Nachrichten";
  }, []);

  const handleAuthError = (err) => {
    if (Number(err?.status) !== 401) return false;
    clearStoredAuth();
    const detail = String(err?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchJson("/api/applicant-dashboard/mission-control");
        if (mounted) setSnapshot(data);
      } catch (err) {
        if (handleAuthError(err)) return;
        if (mounted) setError(err.message || "Die Nachrichten konnten nicht geladen werden.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const notifications = snapshot?.notifications || [];
  const searchItems = useMemo(() => ([
    { href: "/dashboard/applicant", label: "Dashboard", description: "Zur Mission Control" },
    { href: "/dashboard/applicant/nachrichten", label: "Nachrichten", description: "Applicant-Hinweise und Aktionen" },
  ]), []);

  return (
    <ApplicantShell
      title="Nachrichten"
      subtitle="Hier liegen nur deine Applicant-Hinweise und direkten Nachrichtenwege."
      actions={<Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/dashboard/applicant")}>Dashboard</Button>}
      notifications={[]}
      searchItems={searchItems}
    >
      <div className="applicant-workspace-page">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
        <section className="applicant-workspace-hero applicant-workspace-hero--messages">
          <div className="applicant-workspace-hero__main">
            <span className="applicant-section-kicker">Nachrichten</span>
            <h2>Was fuer dich gerade wichtig ist</h2>
            <p>Hier siehst du nur Applicant-Hinweise, offene Schritte und direkte Wege zu den betroffenen Bereichen.</p>
          </div>
        </section>

        <section className="applicant-workspace-card">
          <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Hinweise</span><h3>Deine aktuellen Nachrichten</h3></div></div>
          <div className="applicant-notice-stack">
            {loading ? <div className="applicant-empty-card">Nachrichten werden geladen ...</div> : notifications.length ? notifications.map((item) => (
              <article key={item.key} className="applicant-detail-card applicant-detail-card--message">
                <span className="applicant-section-kicker">Hinweis</span>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
                <button type="button" className="applicant-inline-link" onClick={() => navigate(item.href)}><span>Oeffnen</span><ChevronRight size={15} /></button>
              </article>
            )) : <div className="applicant-empty-card"><MessageSquareMore size={18} /><span>Gerade liegt keine neue Applicant-Nachricht vor.</span></div>}
          </div>
        </section>
      </div>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>Startseite</Button>
            <Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>Erneut anmelden</Button>
          </div>
        </DialogContent>
      </Dialog>
    </ApplicantShell>
  );
}
